import { createSlice } from "@reduxjs/toolkit";
import {
  getMyCoachReview,
  submitReview,
  updateReview,
  getCoachReviews,
} from "./reviewApi";

const initialMyReview = {
  canRate: false,
  existingReview: null,
};

const initialState = {
  reviews: [],
  myReview: initialMyReview,
  isLoading: false,
  isMyReviewLoading: false,
  error: null,
  success: false,
  message: "",
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    hasMore: false,
  },
  justSubmittedReview: false,
};

export const reviewSlice = createSlice({
  name: "review",
  initialState,
  reducers: {
    clearReviewError: (state) => {
      state.error = null;
    },
    resetReviewState: (state) => {
      state.error = null;
      state.success = false;
      state.message = "";
    },
    resetMyReview: (state) => {
      state.myReview = initialMyReview;
    },
    resetPagination: (state) => {
      state.pagination = {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
        hasMore: false,
      };
    },
    clearJustSubmittedReview: (state) => {
      state.justSubmittedReview = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get my coach review
      .addCase(getMyCoachReview.pending, (state) => {
        state.isMyReviewLoading = true;
        state.error = null;
      })
      .addCase(getMyCoachReview.fulfilled, (state, action) => {
        state.isMyReviewLoading = false;
        state.myReview = action.payload.data || initialMyReview;
      })
      .addCase(getMyCoachReview.rejected, (state, action) => {
        state.isMyReviewLoading = false;
        state.myReview = initialMyReview;
        state.error = action.payload?.message || "Failed to fetch review status";
      })

      // Submit review
      .addCase(submitReview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(submitReview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.message =
          action.payload.message || "Review submitted successfully";
        state.error = null;
        state.justSubmittedReview = true;
      })
      .addCase(submitReview.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to submit review";
        state.message = "";
      })

      // Update review
      .addCase(updateReview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateReview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.message = action.payload.message || "Review updated successfully";
        state.error = null;
        state.justSubmittedReview = true;
      })
      .addCase(updateReview.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to update review";
        state.message = "";
      })

      // Get coach reviews
      .addCase(getCoachReviews.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCoachReviews.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.reviews = action.payload.data || [];

        const pagination = action.payload.pagination;
        if (pagination) {
          const { totalCount, hasMore } = pagination;
          const currentPage = action.meta?.arg?.skip
            ? Math.floor(
                action.meta.arg.skip / (action.meta?.arg?.limit || 10),
              ) + 1
            : 1;
          const pageSize = action.meta?.arg?.limit || 10;

          state.pagination = {
            currentPage,
            pageSize,
            totalItems: totalCount,
            totalPages: Math.ceil(totalCount / pageSize),
            hasMore,
          };
        }
      })
      .addCase(getCoachReviews.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to fetch reviews";
        state.reviews = [];
        state.pagination = {
          currentPage: 1,
          pageSize: 10,
          totalItems: 0,
          totalPages: 0,
          hasMore: false,
        };
      });
  },
});

export const {
  clearReviewError,
  resetReviewState,
  resetMyReview,
  resetPagination,
  clearJustSubmittedReview,
} = reviewSlice.actions;
export default reviewSlice.reducer;
